import React, { useEffect, useState } from "react";
import { AdminInterface } from "../../interfaces/admin.interface";
import { createAdmin, updateAdmin } from "../../redux/slices/AdminSlice";
import { useDispatch, useSelector } from "react-redux";
import { DispatchType } from "../../interfaces/redux.interface";
import { successToast } from "../../utils/toast.util";
import { RootState } from "../../redux/store";

const CreateOrUpdateAdminModal = ({
  isOpen,
  closeModal,
  isEdit,
  editData,
}: {
  isOpen: boolean;
  closeModal: () => void;
  isEdit: boolean;
  editData: Partial<AdminInterface>;
}) => {
  const dispatch: DispatchType = useDispatch();
  const { info } = useSelector((state: RootState) => state.info);

  const [adminData, setAdminData] = useState<AdminInterface>({
    name: "",
    phoneCode: "+91",
    phoneNumber: null,
    email: "",
    password: "",
    pinCode: null,
    type: "employee",
    franchisePackAmount: info?.franchiseAdminPack?.amount,
  });

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isEdit && editData) {
      setAdminData({
        name: editData.name || "",
        phoneCode: editData.phoneCode || "+91",
        phoneNumber: editData.phoneNumber || null,
        email: editData.email || "",
        password: editData.password || "",
        pinCode: editData.pinCode || null,
        type: editData.type || "employee",
        franchisePackAmount:
          editData.franchisePackAmount || info?.franchiseAdminPack?.amount,
      });
    } else {
      setAdminData({
        name: "",
        phoneCode: "+91",
        phoneNumber: null,
        email: "",
        password: "",
        pinCode: null,
        type: "employee",
        franchisePackAmount: info?.franchiseAdminPack?.amount,
      });
    }
    setFormErrors({});
  }, [isEdit, editData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setAdminData({ ...adminData, [name]: value });

    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const handleSubmit = () => {
    const errors: { [key: string]: string } = {};

    if (!adminData.name) errors.name = "Name is required";
    if (!adminData.phoneCode) errors.phoneCode = "Phone code is required";
    if (!adminData.phoneNumber) errors.phoneNumber = "Phone number is required";
    if (!adminData.email) errors.email = "Email is required";
    if (!adminData.password) errors.password = "Password is required";
    if (!adminData.pinCode) errors.pinCode = "Pin code is required";

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (isEdit && editData) {
      dispatch(
        updateAdmin({
          id: editData?._id,
          data: { ...adminData },
        })
      )
        .unwrap()
        .then(() => {
          closeModal();
          successToast("Admin updated successfully");
        });
    } else {
      dispatch(createAdmin(adminData))
        .unwrap()
        .then((res) => {
          if (res) {
            closeModal();
            successToast("Admin created successfully");
          }
        });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-lg mx-4 p-6 rounded-lg shadow-lg max-h-screen overflow-auto">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEdit ? "Update Admin" : "Create New Admin"}
          </h2>
        </div>

        <div className="space-y-4">
          <div>
            <input
              type="text"
              name="name"
              value={adminData.name}
              onChange={handleChange}
              placeholder="Name"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {formErrors.name && (
              <p className="text-sm text-red-500">{formErrors.name}</p>
            )}
          </div>

          <div className="flex space-x-2">
            <div className="w-1/3">
              <input
                type="text"
                name="phoneCode"
                value={adminData.phoneCode}
                onChange={handleChange}
                placeholder="Phone Code"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {formErrors.phoneCode && (
                <p className="text-sm text-red-500">{formErrors.phoneCode}</p>
              )}
            </div>
            <div className="w-2/3">
              <input
                type="number"
                name="phoneNumber"
                value={adminData.phoneNumber || ""}
                onChange={handleChange}
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {formErrors.phoneNumber && (
                <p className="text-sm text-red-500">{formErrors.phoneNumber}</p>
              )}
            </div>
          </div>

          <div>
            <input
              type="email"
              name="email"
              value={adminData.email}
              onChange={handleChange}
              placeholder="Email"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {formErrors.email && (
              <p className="text-sm text-red-500">{formErrors.email}</p>
            )}
          </div>

          <div>
            <input
              type="text"
              name="password"
              value={adminData.password}
              onChange={handleChange}
              placeholder="Password"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {formErrors.password && (
              <p className="text-sm text-red-500">{formErrors.password}</p>
            )}
          </div>

          <div>
            <label htmlFor="type" className="block text-left text-gray-700">
              Admin Type
            </label>
            <select
              name="type"
              value={adminData.type}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="employee">Employee</option>
              <option value="franchise">Franchise</option>
            </select>
          </div>

          <div>
            <label htmlFor="pinCode" className="block text-left text-gray-700">
              Pincode
            </label>
            <input
              type="number"
              name="pinCode"
              value={adminData.pinCode || ""}
              onChange={handleChange}
              placeholder="Pin Code"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {formErrors.pinCode && (
              <p className="text-sm text-red-500">{formErrors.pinCode}</p>
            )}
          </div>
          {adminData.type === "franchise" && (
            <div>
              <label
                htmlFor="franchisePackAmount"
                className="block text-left text-gray-700"
              >Enter pack amount</label>
              <input
                type="number"
                name="franchisePackAmount"
                value={adminData.franchisePackAmount || ""}
                onChange={handleChange}
                placeholder="Enter amount"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {formErrors.franchisePackAmount && (
                <p className="text-sm text-red-500">{formErrors.franchisePackAmount}</p>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={closeModal}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-white bg-yellow-600 rounded-md hover:bg-yellow-700 transition disabled:opacity-50"
          >
            {isEdit ? "Update Admin" : "Create Admin"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateOrUpdateAdminModal;
